import getSplashApiClient from 'lib/SplashApiClient/singleton';
import { camelize } from 'utils/textFormats';

/**
 * Wraps a SplashApiClient with Team API actions
 */
export default class VirtualSessionsApiClient {
    /**
     * @param {SplashApiClient} apiClient
     */
    constructor(apiClient) {
        this.apiClient = apiClient;
    }

    postSessions(eventId, body) {
        return this.apiClient.post(`/events/${eventId}/sessions`, body);
    }

    putSessions(eventId, sessionId, body) {
        return this.apiClient.put(`/events/${eventId}/sessions/${sessionId}`, body);
    }

    deleteSession(eventId, sessionId) {
        return this.apiClient.delete(`/events/${eventId}/sessions/${sessionId}`);
    }

    duplicateSession(eventId, sessionId) {
        return this.apiClient.post(`/events/${eventId}/sessions/${sessionId}/duplicate`);
    }

    getSession(eventId, sessionId) {
        return this.apiClient.get(`/events/${eventId}/sessions/${sessionId}`);
    }

    async getPublicSessions({ eventId }) {
        const baseURL = `/public/events/${eventId}/sessions`;
        const response = await this.apiClient.get(baseURL, {}, { omitAccessToken: true });

        return camelize(response.data);
    }

    async getEventListSessions({ eventId, guestAccessHash }) {
        const baseURL = `/events/${eventId}/attendee-registration/sessions`;
        const headers = { 'X-Splash-Guest-Access-Hash': guestAccessHash };
        const response = await this.apiClient.get(baseURL, {}, { headers });

        return camelize(response.data);
    }

    async getAttendeeSessions(eventId, guestAccessHash) {
        const baseURL = `/events/${eventId}/attendee/sessions`;
        const response = await this.apiClient.get(
            baseURL,
            {},
            {
                omitAccessToken: !!guestAccessHash,
                headers: {
                    'X-Splash-Guest-Access-Hash': guestAccessHash,
                },
            },
        );
        return response.data;
    }

    async getAttendeeSessionsStatuses({ eventId, guestAccessHash }) {
        const baseURL = `/events/${eventId}/attendee-registration/sessions/status`;
        const response = await this.apiClient.get(
            baseURL,
            {},
            {
                omitAccessToken: true,
                headers: {
                    'X-Splash-Guest-Access-Hash': guestAccessHash,
                },
            },
        );

        return response.data;
    }

    async registerForSession({ eventId, sessionId, guestAccessHash }) {
        const baseURL = `/events/${eventId}/attendee-registration/sessions/${sessionId}/rsvp`;
        const response = await this.apiClient.post(
            baseURL,
            {},
            {
                omitAccessToken: true,
                headers: {
                    'X-Splash-Guest-Access-Hash': guestAccessHash,
                },
            },
        );

        return response.data;
    }

    async unregisterForSession({ eventId, sessionId, guestAccessHash }) {
        const baseURL = `/events/${eventId}/attendee-registration/sessions/${sessionId}/rsvp`;
        const response = await this.apiClient.delete(
            baseURL,
            {},
            {
                omitAccessToken: true,
                headers: {
                    'X-Splash-Guest-Access-Hash': guestAccessHash,
                },
            },
        );

        return response.data;
    }

    async getSessionCalendarAttachment({ eventId, sessionId, guestAccessHash }) {
        const baseURL = `/events/${eventId}/attendee-registration/sessions/${sessionId}/calendar`;
        const response = await this.apiClient.get(
            baseURL,
            {},
            {
                omitAccessToken: true,
                headers: {
                    'X-Splash-Guest-Access-Hash': guestAccessHash,
                },
            },
        );

        return response.data;
    }

    async getSessionsCalendarsAttachment({ eventId, guestAccessHash }) {
        const baseURL = `/events/${eventId}/attendee-registration/sessions/calendars`;
        const response = await this.apiClient.get(
            baseURL,
            {},
            {
                omitAccessToken: true,
                headers: {
                    'X-Splash-Guest-Access-Hash': guestAccessHash,
                },
            },
        );

        return response.data;
    }
}

export const getVirtualSessionsApiClient = () => new VirtualSessionsApiClient(getSplashApiClient());
